import { CELL, GROUND } from '../core/config';
import type { City, RoadEdge, RoadNode } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import {
  roadHeight,
  roadLaneOffset,
  roadLength,
  roadPoint,
  roadProgress,
  roadSurfaceHeight,
} from '../world/roads';
import { MinQueue } from '../core/priority';
export interface RoutePoint {
  maxSpeed?: number;
  reverse?: boolean;
  junction?: number;
  hold?: number;
  x: number;
  y: number;
  z: number;
  edge?: number;
  node?: number;
  layer?: string;
}
/** Finite connected road graph with live bridge-deck validation and bounded searches. */
export class RoadNetwork {
  nodes = new Map<number, RoadNode>();
  edges = new Map<number, RoadEdge>();
  adjacent = new Map<number, RoadEdge[]>();
  closures = new Set<number>();
  private cache = new Map<number, { open: boolean; until: number }>();
  time = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
  ) {
    for (const node of city.roadGraph?.nodes ?? []) this.nodes.set(node.id, node);
    for (const edge of city.roadGraph?.edges ?? []) {
      this.edges.set(edge.id, edge);
      for (const id of [edge.from, edge.to]) {
        const list = this.adjacent.get(id) ?? [];
        list.push(edge);
        this.adjacent.set(id, list);
      }
    }
  }
  clear(): void {
    this.cache.clear();
    this.closures.clear();
    this.time = 0;
  }
  invalidate(buildingId: number): void {
    for (const edge of this.edges.values())
      if (edge.bridgeId === buildingId || edge.surfaceIds?.includes(buildingId)) this.cache.delete(edge.id);
  }
  nearest(point: { x: number; z: number }): number {
    let best = 0,
      distance = Infinity;
    for (const node of this.nodes.values()) {
      const d = (point.x - node.x) ** 2 + (point.z - node.z) ** 2;
      if (d < distance) {
        best = node.id;
        distance = d;
      }
    }
    return best;
  }
  other(edge: RoadEdge, from: number): number {
    return edge.from === from ? edge.to : edge.from;
  }
  edgeBetween(from: number, to: number): RoadEdge | undefined {
    return this.adjacent.get(from)?.find((edge) => this.other(edge, from) === to);
  }
  height(edge: RoadEdge, x: number, z: number): number {
    if (edge.profile) return edge.curved ? roadSurfaceHeight(edge, x, z) : roadHeight(edge, x, z);
    if (edge.bridgeId === undefined) return GROUND;
    const bridge = this.city.bridges?.find((b) => b.id === edge.bridgeId);
    if (!bridge) return GROUND;
    const a = this.nodes.get(edge.from)!,
      b = this.nodes.get(edge.to)!;
    const low = Math.min(a.x, b.x),
      high = Math.max(a.x, b.x),
      left = bridge.x,
      right = bridge.x + bridge.w * CELL;
    const deck = (bridge.baseY ?? GROUND) + 4 * CELL;
    if (x < left) return GROUND + (deck - GROUND) * Math.max(0, (x - low) / Math.max(0.01, left - low));
    if (x > right) return GROUND + (deck - GROUND) * Math.max(0, (high - x) / Math.max(0.01, high - right));
    void z;
    return deck;
  }
  waypoint(edge: RoadEdge, from: number, t = 1, pavement = false): RoutePoint {
    const a = this.nodes.get(from)!,
      b = this.nodes.get(this.other(edge, from))!;
    const length = Math.hypot(b.x - a.x, b.z - a.z) || 1,
      dx = (b.x - a.x) / length,
      dz = (b.z - a.z) / length;
    const lane = pavement ? edge.width / 2 + 0.75 : roadLaneOffset(edge.width);
    if (edge.curved && edge.profile) {
      const forward = from === edge.from;
      const point = roadPoint(edge, forward ? t : 1 - t, forward ? lane : -lane);
      return {
        ...point,
        y: this.height(edge, point.x, point.z),
        edge: edge.id,
        node: b.id,
        layer: edge.layer,
      };
    }
    const x = a.x + (b.x - a.x) * t - dz * lane,
      z = a.z + (b.z - a.z) * t + dx * lane;
    return {
      x,
      y: this.height(edge, x, z) + (pavement && edge.profile?.length ? 0.12 : 0),
      z,
      edge: edge.id,
      node: b.id,
      layer: edge.layer ?? 'street',
    };
  }
  progress(edge: RoadEdge, from: number, point: { x: number; z: number }): number {
    if (edge.profile) {
      const t = roadProgress(edge, point);
      return from === edge.from ? t : 1 - t;
    }
    const a = this.nodes.get(from)!,
      b = this.nodes.get(this.other(edge, from))!;
    return Math.max(
      0,
      Math.min(
        1,
        ((point.x - a.x) * (b.x - a.x) + (point.z - a.z) * (b.z - a.z)) /
          ((b.x - a.x) ** 2 + (b.z - a.z) ** 2),
      ),
    );
  }
  /** Curves need intermediate goals; driving directly at the next junction cuts land. */
  points(edge: RoadEdge, from: number, start = 0, end = 1): RoutePoint[] {
    if (!edge.curved) return [this.waypoint(edge, from, end)];
    const count = Math.max(1, Math.ceil((Math.abs(end - start) * roadLength(edge)) / 1.25));
    return Array.from({ length: count }, (_, i) => ({
      ...this.waypoint(edge, from, start + ((end - start) * (i + 1)) / count),
      node: i === count - 1 && end === 1 ? this.other(edge, from) : undefined,
    }));
  }
  /** Approach and exit tangents keep a turn within its lanes. */
  roundTurns(
    points: RoutePoint[],
    vehicleLength: number,
    initial: { x: number; y: number; z: number },
  ): RoutePoint[] {
    const result: RoutePoint[] = [];
    for (let i = 0; i < points.length; i++) {
      const p = points[i],
        next = points[i + 1],
        incoming = p.edge === undefined ? undefined : this.edges.get(p.edge),
        outgoing = next?.edge === undefined ? undefined : this.edges.get(next.edge);
      const node = p.node === undefined ? undefined : this.nodes.get(p.node);
      if (
        p.reverse ||
        next?.reverse ||
        !node ||
        !incoming ||
        !outgoing ||
        incoming === outgoing ||
        ![outgoing.from, outgoing.to].includes(node.id)
      ) {
        result.push(p);
        continue;
      }
      const a = this.nodes.get(this.other(incoming, node.id))!,
        b = this.nodes.get(this.other(outgoing, node.id))!;
      const tangent = (edge: RoadEdge, outward: boolean) => {
        const fromStart = edge.from === node.id,
          length = roadLength(edge),
          near = roadPoint(edge, fromStart ? Math.min(1, 4 / length) : Math.max(0, 1 - 4 / length)),
          dx = near.x - node.x,
          dz = near.z - node.z,
          norm = Math.hypot(dx, dz) || 1;
        return { x: (dx / norm) * (outward ? 1 : -1), z: (dz / norm) * (outward ? 1 : -1) };
      };
      const entry = incoming.curved ? tangent(incoming, false) : undefined,
        exit = outgoing.curved ? tangent(outgoing, true) : undefined;
      const inLength = Math.hypot(node.x - a.x, node.z - a.z),
        outLength = Math.hypot(b.x - node.x, b.z - node.z);
      const dx = entry?.x ?? (node.x - a.x) / inLength,
        dz = entry?.z ?? (node.z - a.z) / inLength,
        ex = exit?.x ?? (b.x - node.x) / outLength,
        ez = exit?.z ?? (b.z - node.z) / outLength;
      if (Math.abs(dx * ex + dz * ez) > 0.2) {
        result.push(p);
        continue;
      }
      const reach = Math.min(Math.max(4.5, vehicleLength * 0.9), inLength * 0.3, outLength * 0.3),
        left = roadLaneOffset(incoming.width),
        right = roadLaneOffset(outgoing.width);
      const start = { x: node.x - dx * reach - dz * left, z: node.z - dz * reach + dx * left };
      const end = { x: node.x + ex * reach - ez * right, z: node.z + ez * reach + ex * right };
      const control = { x: node.x - dz * left - ez * right, z: node.z + dx * left + ex * right };
      while (
        result.length &&
        result.at(-1)!.edge === incoming.id &&
        (node.x - result.at(-1)!.x) * dx + (node.z - result.at(-1)!.z) * dz < reach
      )
        result.pop();
      if (i === 0 && Math.hypot(initial.x - node.x, initial.z - node.z) < reach) {
        start.x = initial.x;
        start.z = initial.z;
      }
      const count = Math.max(6, Math.ceil((reach * 2) / 0.9));
      result.push({
        ...start,
        y: this.height(incoming, start.x, start.z),
        edge: incoming.id,
        junction: node.id,
      });
      for (let n = 1; n <= count; n++) {
        const t = n / count,
          s = 1 - t,
          x = s * s * start.x + 2 * s * t * control.x + t * t * end.x,
          z = s * s * start.z + 2 * s * t * control.z + t * t * end.z;
        const edge = n > count / 2 ? outgoing : incoming;
        result.push({
          x,
          y: this.height(edge, x, z),
          z,
          edge: edge.id,
          junction: node.id,
          node: n === count ? node.id : undefined,
        });
      }
      while (
        points[i + 1]?.edge === outgoing.id &&
        outgoing.curved &&
        (points[i + 1].x - node.x) * ex + (points[i + 1].z - node.z) * ez < reach
      )
        i++;
    }
    return result;
  }
  open(edge: RoadEdge, emergency = false, vehicleLength = 0): boolean {
    if (edge.restricted && !emergency) return false;
    if (vehicleLength > (edge.maxVehicleLength ?? Infinity)) return false;
    if (edge.kind === 'airport service' && !emergency) return false;
    if (!emergency && this.closures.has(edge.id)) return false;
    const cached = this.cache.get(edge.id);
    if (cached && cached.until > this.time) return cached.open;
    let open = true;
    if (edge.bridgeId !== undefined) {
      const bridge = this.city.bridges?.find((b) => b.id === edge.bridgeId);
      if (bridge) {
        const supported = new Set<number>();
        for (const cell of this.physics.buildingCells(bridge.id)) {
          if (cell.y !== 3 || Math.abs(cell.z - bridge.d / 2) > 0.9) continue;
          const owner = this.physics.cellOwner.get(cell.id);
          const cluster =
            owner === undefined
              ? undefined
              : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
          if (cluster?.anchored) supported.add(cell.x);
        }
        open = supported.size >= bridge.w;
      }
    }
    if (open) {
      const a = this.nodes.get(edge.from)!,
        b = this.nodes.get(edge.to)!;
      const samples = Math.max(
        2,
        Math.ceil(
          (edge.curved ? roadLength(edge) : Math.hypot(a.x - b.x, a.z - b.z)) / (edge.curved ? 3 : 9),
        ),
      );
      for (let i = 1; i < samples; i++) {
        const p = this.waypoint(edge, edge.from, i / samples);
        if (edge.profile && !this.physics.ground(p, 0.22, 0.15)) {
          open = false;
          break;
        }
        if (this.physics.blocked(p.x, p.z, 0.82, p.y + 1.05)) {
          open = false;
          break;
        }
      }
    }
    this.cache.set(edge.id, { open, until: this.time + 1.5 });
    return open;
  }
  /** Dijkstra, bounded by this finite region's nodes. An empty path means unreachable. */
  route(from: number, to: number, emergency = false, vehicleLength = 0): number[] {
    if (from === to) return [from];
    const distances = new Map<number, number>([[from, 0]]),
      previous = new Map<number, number>(),
      pending = new MinQueue<number>();
    pending.push(from, 0);
    let visits = 0;
    while (pending.size && visits++ <= this.nodes.size * 8) {
      const { value: current, cost } = pending.pop()!;
      if (cost > (distances.get(current) ?? Infinity)) continue;
      if (current === to) break;
      for (const edge of this.adjacent.get(current) ?? []) {
        if (!this.open(edge, emergency, vehicleLength)) continue;
        const next = this.other(edge, current),
          a = this.nodes.get(current)!,
          b = this.nodes.get(next)!;
        const distance = cost + (edge.curved ? roadLength(edge) : Math.hypot(a.x - b.x, a.z - b.z));
        if (distance < (distances.get(next) ?? Infinity)) {
          distances.set(next, distance);
          previous.set(next, current);
          pending.push(next, distance);
        }
      }
    }
    if (!previous.has(to)) return [];
    const path = [to];
    let next = to;
    while (next !== from && path.length <= this.nodes.size) {
      next = previous.get(next)!;
      path.push(next);
    }
    return path.reverse();
  }
  /** Directional routing includes the incoming and final partial roads. It
   * avoids the instantaneous U-turns produced by a node-only shortest path. */
  directionalRoute(
    from: number,
    to: number,
    previous: number | undefined,
    exit: number | undefined,
    emergency: boolean,
    vehicleLength: number,
  ): number[] {
    type State = { node: number; previous?: number; parent?: State; cost: number };
    const first: State = { node: from, previous, cost: 0 },
      pending = new MinQueue<State>(),
      distances = new Map<string, number>();
    const key = (s: State) => `${s.previous ?? -1}:${s.node}`;
    pending.push(first, 0);
    distances.set(key(first), 0);
    let visits = 0;
    while (pending.size && visits++ < Math.max(16, this.edges.size * 8)) {
      const current = pending.pop()!.value;
      if (current.cost > (distances.get(key(current)) ?? Infinity)) continue;
      if (current.node === to && (exit === undefined || current.previous !== exit)) {
        const result: number[] = [];
        for (let s: State | undefined = current; s; s = s.parent) result.push(s.node);
        return result.reverse();
      }
      for (const edge of this.adjacent.get(current.node) ?? []) {
        const next = this.other(edge, current.node);
        if (next === current.previous || !this.open(edge, emergency, vehicleLength)) continue;
        const a = this.nodes.get(current.node)!,
          b = this.nodes.get(next)!;
        const cost = current.cost + (edge.curved ? roadLength(edge) : Math.hypot(b.x - a.x, b.z - a.z));
        const state: State = { node: next, previous: current.node, parent: current, cost };
        if (cost >= (distances.get(key(state)) ?? Infinity)) continue;
        distances.set(key(state), cost);
        pending.push(state, cost);
      }
    }
    return [];
  }
  approach(
    point: { x: number; z: number },
    emergency = true,
    vehicleLength = 0,
  ): { point: RoutePoint; node: number; edge: RoadEdge } | null {
    return this.approaches(point, emergency, vehicleLength, 1)[0] ?? null;
  }
  approaches(
    point: { x: number; z: number },
    emergency = true,
    vehicleLength = 0,
    limit = 6,
  ): { point: RoutePoint; node: number; edge: RoadEdge }[] {
    const best: { point: RoutePoint; node: number; edge: RoadEdge; distance: number }[] = [];
    for (const edge of this.edges.values()) {
      const a = this.nodes.get(edge.from)!,
        b = this.nodes.get(edge.to)!;
      const dx = b.x - a.x,
        dz = b.z - a.z,
        length = dx * dx + dz * dz;
      const t = Math.max(
        0.08,
        Math.min(
          0.92,
          edge.curved
            ? this.progress(edge, edge.from, point)
            : ((point.x - a.x) * dx + (point.z - a.z) * dz) / length,
        ),
      );
      const p = this.waypoint(edge, edge.from, t),
        d = Math.hypot(p.x - point.x, p.z - point.z);
      if ((best.length < limit || d < best.at(-1)!.distance) && this.open(edge, emergency, vehicleLength)) {
        best.push({ point: p, node: t < 0.5 ? edge.from : edge.to, edge, distance: d });
        best.sort((a, b) => a.distance - b.distance);
        if (best.length > limit) best.pop();
      }
    }
    return best;
  }
}
